"use client"

import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react"
import type {
  CommissionerBulkUpdateItem,
  CommissionerDataImportDetail,
  CommissionerReviewItem,
  ImportAs,
  ReviewItemAssociationCounts,
} from "@/schema/commissioner-data-import"
import { Checkbox, Input, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@ui/shad"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@ui/shad/select"
import { Loader2, Search } from "lucide-react"
import { toast } from "sonner"

import { useDataImportApi } from "@/hooks/api/data-import-api/context"
import InfiniteScroll from "@/components/_ui/infinite-scroll"

const IMPORT_AS_OPTIONS: { value: ImportAs; label: string }[] = [
  { value: "client", label: "Client" },
  { value: "past_client", label: "Past client" },
  { value: "prospect", label: "Prospect" },
  { value: "other_contact", label: "Other contact" },
]

function formatAssociationCounts(counts: ReviewItemAssociationCounts): string {
  const parts: string[] = []
  if (counts.tasks > 0) parts.push(`${counts.tasks} task${counts.tasks === 1 ? "" : "s"}`)
  if (counts.notes > 0) parts.push(`${counts.notes} note${counts.notes === 1 ? "" : "s"}`)
  if (counts.events > 0) parts.push(`${counts.events} event${counts.events === 1 ? "" : "s"}`)
  if (counts.opportunities > 0)
    parts.push(`${counts.opportunities} opportunit${counts.opportunities === 1 ? "y" : "ies"}`)
  if (counts.trusts && counts.trusts > 0) parts.push(`${counts.trusts} trust${counts.trusts === 1 ? "" : "s"}`)
  if (counts.workflows && counts.workflows > 0)
    parts.push(`${counts.workflows} workflow${counts.workflows === 1 ? "" : "s"}`)
  return parts.join(", ")
}

export interface ReviewStepHandle {
  startImport: () => Promise<void>
  saveChanges: () => Promise<void>
}

export interface ReviewStepStatus {
  pendingCount: number
  includedCount: number
  isStartPending: boolean
  isSavePending: boolean
}

export const ReviewStep = forwardRef<
  ReviewStepHandle,
  { dataImport: CommissionerDataImportDetail; onStatusChange: (status: ReviewStepStatus) => void }
>(function ReviewStep({ dataImport, onStatusChange }, ref) {
  const [searchQuery, setSearchQuery] = useState("")
  const [debouncedQuery, setDebouncedQuery] = useState("")

  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(searchQuery.trim()), 300)
    return () => clearTimeout(id)
  }, [searchQuery])

  const api = useDataImportApi()
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = api.useFetchReviewItems(dataImport.id, {
    query: debouncedQuery || undefined,
    per_page: 50,
  })
  const bulkUpdate = api.useBulkUpdateReviewItems(dataImport.id)
  const startImportMutation = api.useStartImport(dataImport.id)

  const reviewItems = useMemo(() => data?.review_items ?? [], [data])
  const totalCount =
    data?.pagination?.total_count ?? dataImport.stats.total_households + dataImport.stats.total_businesses

  const [pending, setPending] = useState<Map<string, CommissionerBulkUpdateItem>>(new Map())

  const itemKey = (item: Pick<CommissionerReviewItem, "type" | "id">) => `${item.type}-${item.id}`

  const displayItem = useCallback(
    (item: CommissionerReviewItem): CommissionerReviewItem => {
      const change = pending.get(itemKey(item))
      if (!change) return item
      return {
        ...item,
        will_be_included: change.included ?? item.will_be_included,
        import_as: change.import_as !== undefined ? change.import_as : item.import_as,
      }
    },
    [pending]
  )

  const updateChange = (item: CommissionerReviewItem, change: Partial<CommissionerBulkUpdateItem>) => {
    setPending((prev) => {
      const key = itemKey(item)
      const existing = prev.get(key)
      const next = new Map(prev)
      next.set(key, { type: item.type, id: item.id, ...existing, ...change })
      return next
    })
  }

  const saveChanges = useCallback(async () => {
    if (pending.size === 0) return
    await bulkUpdate.mutateAsync(Array.from(pending.values()))
    setPending(new Map())
    toast.success("Review changes saved")
  }, [bulkUpdate, pending])

  const startImport = useCallback(async () => {
    if (pending.size > 0) {
      await bulkUpdate.mutateAsync(Array.from(pending.values()))
      setPending(new Map())
    }
    await startImportMutation.mutateAsync()
    toast.success("Import started")
  }, [bulkUpdate, pending, startImportMutation])

  const includedCount = useMemo(() => {
    let delta = 0
    pending.forEach((change, key) => {
      if (change.included === undefined) return
      const rawItem = reviewItems.find((item) => itemKey(item) === key)
      if (!rawItem) return
      if (change.included !== rawItem.will_be_included) {
        delta += change.included ? 1 : -1
      }
    })
    return dataImport.stats.included + delta
  }, [dataImport.stats.included, pending, reviewItems])

  useImperativeHandle(ref, () => ({ startImport, saveChanges }), [startImport, saveChanges])

  useEffect(() => {
    onStatusChange({
      pendingCount: pending.size,
      includedCount,
      isStartPending: startImportMutation.isPending,
      isSavePending: bulkUpdate.isPending,
    })
  }, [pending.size, includedCount, startImportMutation.isPending, bulkUpdate.isPending, onStatusChange])

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <div>
        <h3 className="text-lg font-medium">Review</h3>
        <p className="text-muted-foreground text-sm">
          {includedCount} of {totalCount} rows will be imported into this book.
        </p>
      </div>

      <div className="relative">
        <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
        <Input
          placeholder="Search by name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="animate-spin" />
        </div>
      ) : (
        <>
          <div className="overflow-hidden rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Include</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Import as</TableHead>
                  <TableHead>People</TableHead>
                  <TableHead>Associated records</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reviewItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-muted-foreground py-8 text-center text-sm">
                      No rows match your search.
                    </TableCell>
                  </TableRow>
                ) : (
                  reviewItems.map((rawItem) => {
                    const item = displayItem(rawItem)
                    const contactInfo = [item.primary_email, item.primary_phone].filter(Boolean).join(" · ")
                    const associationSummary = formatAssociationCounts(item.association_counts)
                    return (
                      <TableRow key={itemKey(item)}>
                        <TableCell>
                          <Checkbox
                            checked={item.will_be_included}
                            onCheckedChange={(checked) => updateChange(item, { included: checked === true })}
                          />
                        </TableCell>
                        <TableCell className="font-medium">
                          <div>{item.name}</div>
                          {item.head_of_household_name && (
                            <div className="text-muted-foreground text-xs">{item.head_of_household_name}</div>
                          )}
                          {contactInfo && <div className="text-muted-foreground text-xs">{contactInfo}</div>}
                        </TableCell>
                        <TableCell>
                          {item.type === "household" ? (
                            <Select
                              value={item.import_as ?? undefined}
                              onValueChange={(v) => updateChange(item, { import_as: v as ImportAs })}
                            >
                              <SelectTrigger className="h-8 w-36">
                                <SelectValue placeholder="—" />
                              </SelectTrigger>
                              <SelectContent>
                                {IMPORT_AS_OPTIONS.map((opt) => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className="text-muted-foreground text-sm">Business</span>
                          )}
                        </TableCell>
                        <TableCell>{item.people_count || "—"}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{associationSummary || "—"}</TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </div>

          <InfiniteScroll
            isFetchingNextPage={isFetchingNextPage}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
          />
        </>
      )}
    </div>
  )
})
